'use client';

import { Button, CheckboxField } from '@petsmart-ui/sparky';
import type { CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCurrentRefinements, useInstantSearch } from 'react-instantsearch';

import { configDynamicFacets, FILTERS } from '@libs/algolia';
import { dataLayerPush, trackApplyFacetedFilters, trackPLPFilters } from '@libs/analytics';
import { clsx, RenderOnClient, useStore } from '@libs/common-next';
import { isStoreBopisEnabled, stringifyStoreId } from '@libs/core';

import type { FindStoreNearMeProps } from '@/components/FindStoreNearMe';
import { closeButtonDropdown } from '@/utils/closeButtonDropdown';

import '@/components/FilterRefinementList/FilterRefinementList.scss';
import '@/components/FindStoreNearMe/FindStoreNearMe.scss';

export const getCountStore = (items: CurrentRefinementsConnectorParamsItem[]) => {
    const inventory = items.find(item => item.attribute === FILTERS.SHOP_BY_STORE.ATTRIBUTE);
    const [refinement] = inventory?.refinements ?? [];
    return refinement?.count ?? 0;
};

export const storeMatchesSelected = (storeNumberStr: string, refinedItems: CurrentRefinementsConnectorParamsItem[]) => {
    const expected = stringifyStoreId(storeNumberStr);
    if (!expected) {
        return false;
    }
    const inventoryItem = refinedItems.find(item => item.attribute === FILTERS.SHOP_BY_STORE.ATTRIBUTE);
    return Boolean(inventoryItem?.refinements?.some(r => stringifyStoreId(String(r.value)) === expected));
};

export default function FindStoreNearMe({
    openStoreModal,
    showActions,
    clearShop,
    pageId,
    handleIsStoreFilterApplied,
}: Readonly<FindStoreNearMeProps>) {
    const { selectedStore } = useStore();
    const { data: store } = selectedStore;
    const storeHasBopis = isStoreBopisEnabled(store);
    const isShopByStoreCheckboxDisabled = Boolean(store && !storeHasBopis);

    const { setIndexUiState } = useInstantSearch();

    const { items: refinedItems } = useCurrentRefinements({
        includedAttributes: [FILTERS.SHOP_BY_STORE.ATTRIBUTE],
    });

    const isSelectingNewStore = useRef<string>(''); // Used to track if we have opened and selected a new store (value: 'openModal' || '')
    const [isChecked, setIsChecked] = useState<boolean>(false);

    const openModal = () => {
        closeButtonDropdown();
        openStoreModal();
        isSelectingNewStore.current = 'openModal';
    };

    // Both keys are SET (not toggled): SHOP_BY_STORE becomes exactly `[storeNumber]`
    // and BOPIS_ELIGIBLE is forced on. Clearing both is handled by `clearShop`
    // (`useClearRefinements({ includedAttributes: [SHOP_BY_STORE, BOPIS_ELIGIBLE] })`)
    // via `handleOnCheckboxChange(false)`. Using `setIndexUiState` instead of two
    // separate `refine()` calls collapses the change into a single Algolia request.
    const applyStoreFilter = useCallback(
        (storeNumber: string) => {
            setIndexUiState(prev => ({
                ...prev,
                refinementList: {
                    ...prev.refinementList,
                    [FILTERS.SHOP_BY_STORE.ATTRIBUTE]: [storeNumber],
                    [FILTERS.BOPIS_ELIGIBLE.ATTRIBUTE]: [FILTERS.BOPIS_ELIGIBLE.DEFAULT_VALUE],
                },
            }));
        },
        [setIndexUiState]
    );

    const handleOnCheckboxChange = (val: boolean) => {
        setIsChecked(val);

        if (store && val && storeHasBopis) {
            applyStoreFilter(store.StoreNumber.toString());

            if (store.Name) {
                const value = store.Name;
                const title = configDynamicFacets[FILTERS.SHOP_BY_STORE.ATTRIBUTE]?.title;

                dataLayerPush({
                    event: FILTERS.EVENT.VALUE,
                    filter_type: title,
                    filter_selection: value,
                    results: getCountStore(refinedItems),
                });
                trackPLPFilters(title, value);
                trackApplyFacetedFilters(title, value, pageId);
            }
        } else {
            clearShop();
        }

        isSelectingNewStore.current = '';
    };

    useEffect(() => {
        if (!store || storeHasBopis) {
            return;
        }

        setIsChecked(false);

        // useCurrentRefinements is scoped to SHOP_BY_STORE only, so length > 0 means that filter is active.
        if (refinedItems.length > 0) {
            clearShop();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: refinedItems ref is unstable; length matches adjacent store-sync effect
    }, [store, storeHasBopis, refinedItems.length, clearShop]);

    // This effect is used to update search when we select a new store
    useEffect(
        () => {
            if (!store?.StoreNumber || !storeHasBopis) {
                return;
            }

            const storeNumberStr = store.StoreNumber.toString();
            if (refinedItems.length > 0) {
                if (!storeMatchesSelected(storeNumberStr, refinedItems)) {
                    applyStoreFilter(storeNumberStr);
                }
                setIsChecked(true);
            } else if (isSelectingNewStore.current === 'openModal' && refinedItems.length === 0) {
                applyStoreFilter(storeNumberStr);

                setIsChecked(true);
                isSelectingNewStore.current = '';
            } else if (isSelectingNewStore.current === '' && refinedItems.length === 0) {
                setIsChecked(false);
            }
        },
        // `refinedItems.length` (not the array reference) is intentional: react-instantsearch
        // returns a new `items` array on every helper update (search results, facet stats);
        // depending on the reference would race the `storeMatchesSelected` check.
        // We only need to react to count transitions (0↔1).
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only react to refinedItems count transitions; full `refinedItems` deps race storeMatchesSelected
        [store, storeHasBopis, applyStoreFilter, refinedItems.length]
    );

    useEffect(() => {
        const hasStoreFilter = refinedItems.find(
            (e: CurrentRefinementsConnectorParamsItem) => e.attribute === FILTERS.SHOP_BY_STORE.ATTRIBUTE
        );
        if (!hasStoreFilter) {
            setIsChecked(false);
        }
    }, [refinedItems]);

    useEffect(() => {
        if (handleIsStoreFilterApplied) {
            handleIsStoreFilterApplied(isChecked);
        }
    }, [isChecked, handleIsStoreFilterApplied]);

    const classNames = clsx('find-store-near-me', isChecked && 'filter-refinement-store--selected');

    const classNamesStore = clsx('find-store-near-me__selections', !store && 'find-store-near-me__selections-no-store');
    return (
        <RenderOnClient>
            <div className={classNames}>
                <div className={classNamesStore}>
                    {!store ? (
                        <Button
                            data-testid="find-store-near-me-button"
                            text={FILTERS.SHOP_BY_STORE.NEAR_ME}
                            variant="link"
                            onClick={openModal}
                        />
                    ) : (
                        <div className="find-store-near-me__filter" data-testid="find-store-near-me-filter">
                            <CheckboxField
                                className="sparky-u-margin-bottom-lg "
                                hideLegend
                                label={FILTERS.SHOP_BY_STORE.CHOOSE_STORE}
                            >
                                {/* the 'key' here is a hack so that we can control the visual checkmark with isChecked */}
                                {/* https://stackoverflow.com/questions/43476729/react-checkbox-does-not-update */}
                                <CheckboxField.Item
                                    key={`shop-checkbox${isChecked ? '--checked' : ''}`}
                                    text={store.Name}
                                    onChange={handleOnCheckboxChange}
                                    checked={isChecked}
                                    disabled={isShopByStoreCheckboxDisabled}
                                />
                            </CheckboxField>
                            <Button text={FILTERS.SHOP_BY_STORE.CHANGE_STORE} variant="link" onClick={openModal} />
                        </div>
                    )}
                </div>
                {showActions && (
                    <div className="custom-filter-refinement-list__actions">
                        <Button
                            data-testid="filter-refinement-list-store-button-reset-values"
                            fullWidth
                            onClick={clearShop}
                            text={FILTERS.SHOP_BY_STORE.CLEAR}
                            type="button"
                            variant="link"
                            className="custom-filter-refinement-list__actions--reset sparky-u-padding-sm sparky-u-margin-bottom-sm"
                            disabled={!isChecked}
                        />
                        <Button
                            data-testid="filter-refinement-list-store-button-close"
                            fullWidth
                            onClick={closeButtonDropdown}
                            text={FILTERS.SHOP_BY_STORE.VIEW_RESULTS}
                            type="button"
                            variant="primary"
                            className="custom-filter-refinement-list__actions--view"
                        />
                    </div>
                )}
            </div>
        </RenderOnClient>
    );
}
